import React from "react";
import { Button, Pill } from "@truevault/aquilo";

import { PrivacyCenterIntegrationVendorDto } from "../../../../common/service/server/dto/PrivacyCenterIntegrationsDto";
import { Tooltip } from "@mui/material";

import "../../../../../sass/components/organisms/vendor/VendorIntegrationConfigurationCard.scss";

type VendorIntegrationConfigurationCardProps = {
  vendor: PrivacyCenterIntegrationVendorDto;
  onConnectClick: (vendor: PrivacyCenterIntegrationVendorDto) => void;
  onEditClick: (vendor: PrivacyCenterIntegrationVendorDto) => void;
  loading?: boolean;
};

const getCardStyle = (vendor: PrivacyCenterIntegrationVendorDto) => {
  return {
    borderColor: `var(--${
      vendor.connected
        ? "color-success-main"
        : vendor.connectionError
        ? "color-error-main"
        : "color-info-main"
    })`,
  };
};

export const VendorIntegrationConfigurationCard = ({
  vendor,
  onConnectClick,
  onEditClick,
  loading,
}: VendorIntegrationConfigurationCardProps) => {
  const isDownstreamVendor = vendor.vendorAutomationName.includes("-downstream");

  const getConnectionBadge = () => {
    if (vendor.connectionError) {
      return (
        <div className="tw-boundary">
          <Pill variant="error">Error</Pill>
        </div>
      );
    }
    if (vendor.connected) {
      if (isDownstreamVendor) {
        // Future: POL-5662: To support more than Shopify as an upstream vendor, need to pass the upstream vendor name here:
        return (
          <div className="tw-boundary">
            <Pill variant="completed">Connected via Shopify</Pill>
          </div>
        );
      } else {
        return (
          <div className="tw-boundary">
            <Pill variant="completed">Connected</Pill>
          </div>
        );
      }
    }
    return (
      <div className="tw-boundary">
        <Pill variant="default">Not Connected</Pill>
      </div>
    );
  };

  /**
   * Generate the badge indicating the *upstream* vendor for a downstream vendor.
   */
  const getUpstreamBadge = () => {
    if (!isDownstreamVendor) {
      return null;
    }

    if (!vendor.connected) {
      // Future: POL-5662: To support more than Shopify as an upstream vendor, need to pass the upstream vendor name here:
      return (
        <Tooltip title="Vendor will connect when Shopify is connected.">
          <div className="tw-boundary">
            <Pill variant="info">Shopify App</Pill>
          </div>
        </Tooltip>
      );
    }
    return null;
  };

  const getButton = () => {
    if (isDownstreamVendor) {
      // Downstream vendors are managed entirely by their upstream and have no independent connection options.
      return null;
    }

    return vendor.connected ? (
      <div className="tw-boundary">
        <Button
          variant="pill"
          color="tertiary"
          onClick={() => {
            onEditClick(vendor);
          }}
          disabled={loading}
        >
          Edit Integration
        </Button>
      </div>
    ) : (
      <div className="tw-boundary">
        <Button
          color="primary"
          disabled={loading}
          size="s"
          onClick={() => {
            onConnectClick(vendor);
          }}
        >
          Connect
        </Button>
      </div>
    );
  };

  return (
    <div className={`vic-card ${loading ? "opacity-50" : ""}`} style={getCardStyle(vendor)}>
      <div className="vic-card-row">
        <img
          className="vic-card-vendor-logo"
          src={`/images/${vendor.vendorIconURL}`}
          alt={`${vendor.vendorName} logo`}
        />
        <div className="flex-grow"></div>
        {getConnectionBadge()}
        {getUpstreamBadge()}
      </div>
      <div className="vic-card-row">
        <h4 className="vic-card-vendor-title">{vendor.vendorName}</h4>
        {getButton()}
      </div>
    </div>
  );
};
